import React, { useState, useRef } from 'react';
import {
  getQuote, getHistory, searchStocks,
  formatPrice, formatMarketCap, formatVolume,
  changeColor, changeSign,
} from '../../services/indiaAPI';
import { askJarvis } from '../../services/claudeAI';
import { useJarvisContext } from '../../context/JarvisContext';
import { THEME } from '../../styles/theme';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const S = {
  wrap:      { padding: '20px', maxWidth: '1100px', margin: '0 auto' },
  title:     { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '20px' },

  searchRow: { display: 'flex', gap: '10px', marginBottom: '24px' },
  input:     { flex: 1, padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: `1px solid rgba(0,212,255,0.2)`, borderRadius: '8px', color: '#fff', fontSize: '15px', outline: 'none', fontFamily: THEME.fonts.body },
  btn:       (col='#00d4ff') => ({ padding: '12px 20px', borderRadius: '8px', border: 'none', background: col, color: '#000', cursor: 'pointer', fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: THEME.fonts.hud }),
  btnGhost:  { padding: '10px 16px', borderRadius: '8px', border: `1px solid rgba(0,212,255,0.3)`, background: 'transparent', color: THEME.colors.blue, cursor: 'pointer', fontSize: '12px' },

  suggest:   { position: 'absolute', top: '100%', left: 0, right: 0, background: '#0f172a', border: `1px solid rgba(0,212,255,0.2)`, borderRadius: '8px', zIndex: 50, marginTop: '4px', overflow: 'hidden' },
  sugItem:   { padding: '10px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  sugName:   { fontSize: '13px', color: '#e2e8f0', fontWeight: '600' },
  sugMeta:   { fontSize: '11px', color: 'rgba(255,255,255,0.4)' },

  card:      { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  cardGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' },

  statBox:   { background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '12px 14px' },
  statLabel: { fontSize: '10px', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', marginBottom: '4px' },
  statVal:   (col='#fff') => ({ fontSize: '16px', fontWeight: '700', color: col, fontFamily: THEME.fonts.hud }),

  priceHero: { marginBottom: '20px' },
  priceBig:  { fontSize: '36px', fontWeight: '900', color: '#fff', fontFamily: THEME.fonts.hud, letterSpacing: '2px' },
  priceChg:  (pct) => ({ fontSize: '16px', fontWeight: '700', color: changeColor(pct), marginLeft: '12px' }),
  stockName: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' },
  badge:     (color) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', background: color + '22', color, border: `1px solid ${color}44`, marginRight: '6px' }),

  barWrap:   { marginBottom: '8px' },
  barLabel:  { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px' },
  barBg:     { background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' },
  barFill:   (pct, color) => ({ width: `${pct}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.5s' }),

  aiCard:    { background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  aiTitle:   { fontSize: '12px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.blue, marginBottom: '14px', fontFamily: THEME.fonts.hud },
  aiText:    { fontSize: '14px', color: '#e2e8f0', lineHeight: '1.7', whiteSpace: 'pre-wrap' },

  error:     { color: '#ef4444', fontSize: '14px', padding: '20px 0' },
  loading:   { textAlign: 'center', padding: '40px', color: THEME.colors.textMuted, fontSize: '14px' },

  chartWrap: { height: '180px', marginTop: '8px' },
  tabRow:    { display: 'flex', gap: '8px', marginBottom: '12px' },
  tab:       (active) => ({ padding: '5px 14px', borderRadius: '6px', border: `1px solid ${active ? THEME.colors.blue : 'rgba(255,255,255,0.1)'}`, background: active ? 'rgba(0,212,255,0.1)' : 'transparent', color: active ? THEME.colors.blue : '#9ca3af', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }),
};

const QUICK = [
  { symbol: 'RELIANCE.NS', name: 'Reliance' },
  { symbol: 'TCS.NS',      name: 'TCS' },
  { symbol: 'HDFCBANK.NS', name: 'HDFC Bank' },
  { symbol: 'INFY.NS',     name: 'Infosys' },
  { symbol: 'SBIN.NS',     name: 'SBI' },
  { symbol: 'ZOMATO.NS',   name: 'Zomato' },
];

export default function StockAnalyzer() {
  const { state } = useJarvisContext();
  const [query,      setQuery]      = useState('');
  const [suggestions,setSuggestions]= useState([]);
  const [quote,      setQuote]      = useState(null);
  const [history,    setHistory]    = useState([]);
  const [period,     setPeriod]     = useState('3mo');
  const [loading,    setLoading]    = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading,  setAiLoading]  = useState(false);
  const [error,      setError]      = useState('');
  const searchRef = useRef(null);

  const handleSearch = async (e) => {
    const v = e.target.value;
    setQuery(v);
    if (v.length < 2) { setSuggestions([]); return; }
    try {
      const res = await searchStocks(v);
      setSuggestions(res);
    } catch {}
  };

  const loadStock = async (symbol) => {
    setSuggestions([]);
    setQuery('');
    setError('');
    setAiAnalysis('');
    setLoading(true);
    try {
      const [q, h] = await Promise.all([
        getQuote(symbol),
        getHistory(symbol, period),
      ]);
      setQuote(q);
      setHistory(h);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const changePeriod = async (p) => {
    if (!quote) return;
    setPeriod(p);
    try {
      const h = await getHistory(quote.symbol, p);
      setHistory(h);
    } catch {}
  };

  const runAIAnalysis = async () => {
    if (!quote) return;
    setAiLoading(true);
    setAiAnalysis('');

    const prompt = `You are JARVIS, an Indian stock market expert. Analyze this stock for Sir and give a complete trading recommendation.

STOCK: ${quote.name} (${quote.symbol})
CURRENT PRICE: ₹${quote.price}
CHANGE TODAY: ${changeSign(quote.changePct)}${quote.changePct?.toFixed(2)}%
OPEN: ₹${quote.open}  HIGH: ₹${quote.high}  LOW: ₹${quote.low}
52W HIGH: ₹${quote.week52High}  52W LOW: ₹${quote.week52Low}
52W POSITION: ${quote.week52Pos}% (0=at low, 100=at high)
VOLUME: ${formatVolume(quote.volume)} (Avg: ${formatVolume(quote.avgVolume)})
MARKET CAP: ${formatMarketCap(quote.marketCap)}
SECTOR: ${quote.sector}
INDEX: ${quote.niftyIndex}
F&O: ${quote.isFnO ? 'Yes (Lot: ' + quote.lotSize + ' shares)' : 'No'}
PE RATIO: ${quote.pe ? quote.pe.toFixed(1) : 'N/A'}
FORWARD PE: ${quote.forwardPE ? quote.forwardPE.toFixed(1) : 'N/A'}
BOOK VALUE: ${quote.bookValue ? '₹' + quote.bookValue.toFixed(1) : 'N/A'}
ROE: ${quote.roe ? (quote.roe * 100).toFixed(1) + '%' : 'N/A'}
DEBT/EQUITY: ${quote.debtToEquity ? quote.debtToEquity.toFixed(2) : 'N/A'}

Please provide:
1. TREND ANALYSIS — Is it bullish, bearish, or sideways? Why?
2. KEY LEVELS — Support and resistance levels (based on 52W range and current price)
3. FUNDAMENTAL SCORE — Is valuation cheap/fair/expensive? (based on PE, ROE, debt)
4. ENTRY ZONE — Best price range to enter (₹)
5. STOP LOSS — Where to place SL (₹)
6. TARGET 1 — First target (₹) and % gain
7. TARGET 2 — Second target (₹) and % gain
8. RISK:REWARD — R:R ratio
9. GRADE — A+/A/B/C/D
10. VERDICT — Should Sir trade this? YES/NO/WAIT and why in 2 lines.

${quote.isFnO ? `F&O NOTE: This stock has F&O. Lot size = ${quote.lotSize}. Calculate approximate P&L per lot for the trade.` : ''}

Be specific with ₹ prices. Address Sir directly. Keep it concise but complete.`;

    try {
      const msgs = [{ role: 'user', content: prompt }];
      const resp = await askJarvis(msgs, '', 1500);
      setAiAnalysis(resp);
    } catch (e) {
      setAiAnalysis('Analysis failed: ' + e.message);
    }
    setAiLoading(false);
  };

  const week52Pos = quote?.week52Pos ?? null;

  return (
    <div style={S.wrap}>
      <div style={S.title}>STOCK ANALYZER</div>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <div style={S.searchRow}>
          <div style={{ position: 'relative', flex: 1 }} ref={searchRef}>
            <input
              style={S.input}
              placeholder="Search stock — RELIANCE, TCS, HDFC Bank..."
              value={query}
              onChange={handleSearch}
              onKeyDown={e => { if (e.key === 'Escape') setSuggestions([]); }}
            />
            {suggestions.length > 0 && (
              <div style={S.suggest}>
                {suggestions.map(s => (
                  <div key={s.symbol} style={S.sugItem} onClick={() => loadStock(s.symbol)}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div>
                      <div style={S.sugName}>{s.name}</div>
                      <div style={S.sugMeta}>{s.symbol} · {s.sector}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={S.sugMeta}>{s.niftyIndex === 'NIFTY50' ? 'Nifty 50' : 'Next 50'}</div>
                      {s.isFnO && <div style={S.badge('#f59e0b')}>F&O</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick picks */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {QUICK.map(q => (
            <button key={q.symbol} style={S.btnGhost} onClick={() => loadStock(q.symbol)}>
              {q.name}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={S.loading}>Loading {quote ? 'chart' : 'stock data'}…</div>}
      {error   && <div style={S.error}>Error: {error}</div>}

      {quote && !loading && (
        <>
          {/* Price Hero */}
          <div style={S.card}>
            <div style={S.priceHero}>
              <div>
                <span style={S.priceBig}>{formatPrice(quote.price)}</span>
                <span style={S.priceChg(quote.changePct)}>
                  {changeSign(quote.changePct)}{quote.changePct?.toFixed(2)}%&nbsp;
                  ({changeSign(quote.change)}{quote.change?.toFixed(2)})
                </span>
              </div>
              <div style={S.stockName}>
                {quote.name} &nbsp;·&nbsp; {quote.symbol}
                &nbsp;·&nbsp;
                <span style={S.badge('#00d4ff')}>{quote.niftyIndex === 'NIFTY50' ? 'Nifty 50' : 'Nifty Next 50'}</span>
                <span style={S.badge('#6b7280')}>{quote.sector}</span>
                {quote.isFnO && <span style={S.badge('#f59e0b')}>F&O  Lot:{quote.lotSize}</span>}
              </div>
            </div>

            {/* 52-Week Range Bar */}
            {week52Pos != null && (
              <div style={S.barWrap}>
                <div style={S.barLabel}>
                  <span>52W Low  {formatPrice(quote.week52Low)}</span>
                  <span>Position: {week52Pos}%</span>
                  <span>52W High  {formatPrice(quote.week52High)}</span>
                </div>
                <div style={S.barBg}>
                  <div style={S.barFill(week52Pos, week52Pos > 70 ? '#10b981' : week52Pos < 30 ? '#ef4444' : '#f59e0b')} />
                </div>
              </div>
            )}

            {/* OHLCV grid */}
            <div style={{ ...S.cardGrid, marginTop: '16px' }}>
              {[
                { label: 'OPEN',     value: formatPrice(quote.open) },
                { label: "TODAY'S HIGH", value: formatPrice(quote.high) },
                { label: "TODAY'S LOW",  value: formatPrice(quote.low) },
                { label: 'PREV CLOSE',   value: formatPrice(quote.prevClose) },
                { label: 'VOLUME',       value: formatVolume(quote.volume) },
                { label: 'AVG VOLUME',   value: formatVolume(quote.avgVolume) },
                { label: 'MARKET CAP',   value: formatMarketCap(quote.marketCap) },
                { label: 'INDUSTRY',     value: quote.industry || quote.sector || '—' },
              ].map(item => (
                <div key={item.label} style={S.statBox}>
                  <div style={S.statLabel}>{item.label}</div>
                  <div style={S.statVal()}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Fundamentals */}
          <div style={S.card}>
            <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '14px' }}>FUNDAMENTALS</div>
            <div style={S.cardGrid}>
              {[
                { label: 'P/E RATIO',    value: quote.pe          ? quote.pe.toFixed(1)           : '—' },
                { label: 'FORWARD P/E',  value: quote.forwardPE   ? quote.forwardPE.toFixed(1)    : '—' },
                { label: 'EPS (TTM)',    value: quote.eps         ? '₹' + quote.eps.toFixed(2)     : '—' },
                { label: 'BOOK VALUE',   value: quote.bookValue   ? '₹' + quote.bookValue.toFixed(1) : '—' },
                { label: 'P/B RATIO',    value: quote.pbRatio     ? quote.pbRatio.toFixed(2)       : '—' },
                { label: 'DIV YIELD',    value: quote.divYield    ? (quote.divYield * 100).toFixed(2) + '%' : '—' },
                { label: 'ROE',          value: quote.roe         ? (quote.roe * 100).toFixed(1) + '%' : '—', col: quote.roe > 0.15 ? '#10b981' : quote.roe > 0 ? '#f59e0b' : '#ef4444' },
                { label: 'DEBT/EQUITY',  value: quote.debtToEquity ? quote.debtToEquity.toFixed(2) : '—', col: quote.debtToEquity < 0.5 ? '#10b981' : quote.debtToEquity < 1.5 ? '#f59e0b' : '#ef4444' },
              ].map(item => (
                <div key={item.label} style={S.statBox}>
                  <div style={S.statLabel}>{item.label}</div>
                  <div style={S.statVal(item.col)}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Price Chart */}
          {history.length > 0 && (
            <div style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)' }}>PRICE CHART</div>
                <div style={S.tabRow}>
                  {['1mo','3mo','6mo','1y'].map(p => (
                    <button key={p} style={S.tab(period === p)} onClick={() => changePeriod(p)}>{p.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              <div style={S.chartWrap}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={history}>
                    <defs>
                      <linearGradient id="stockGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={changeColor(quote.changePct)} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={changeColor(quote.changePct)} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis domain={['auto','auto']} tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={v => '₹'+v.toLocaleString('en-IN')} width={70}/>
                    <Tooltip
                      contentStyle={{ background: '#0f172a', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px', color: '#fff' }}
                      formatter={v => ['₹' + v?.toFixed(2), 'Price']}
                    />
                    <Area type="monotone" dataKey="close" stroke={changeColor(quote.changePct)} fill="url(#stockGrad)" strokeWidth={2} dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* JARVIS AI Analysis */}
          <div style={S.aiCard}>
            <div style={S.aiTitle}>JARVIS AI ANALYSIS</div>
            {!aiAnalysis && !aiLoading && (
              <button style={S.btn()} onClick={runAIAnalysis}>
                Analyze {quote.shortName || quote.name} — Get Entry/SL/TP
              </button>
            )}
            {aiLoading && <div style={{ color: THEME.colors.textMuted, fontSize: '14px' }}>JARVIS is analyzing {quote.name}…</div>}
            {aiAnalysis && (
              <>
                <div style={S.aiText}>{aiAnalysis}</div>
                <button style={{ ...S.btnGhost, marginTop: '14px' }} onClick={runAIAnalysis}>
                  Re-analyze
                </button>
              </>
            )}
          </div>
        </>
      )}

      {!quote && !loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: THEME.colors.textMuted }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📈</div>
          <div style={{ fontSize: '15px', marginBottom: '6px' }}>Search any NSE stock above</div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>
            Nifty 50 + Nifty Next 50 — 100 stocks with live data + JARVIS AI analysis
          </div>
        </div>
      )}
    </div>
  );
}
